/**
 * The field table (docs/ux/loi.md): FIELD, MACHINE, REVIEWED and DECISION
 * for every field of the catalogue, grouped A to F; every row shows the
 * machine value beside the reviewed value and a decision word; the row being
 * decided expands in place into the editor. The machine cell is never
 * editable. Rows are buttons with a roving tabindex.
 */

import { cn } from 'cn';
import { ChevronRightIcon } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import type { LoiFieldDecisionInput, LoiFieldView, LoiValue } from '@/app/types';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { longDate } from '@/lib/format';
import { DecisionMark } from '../casefile/ReviewQueue';
import { DECISION_WORD, MACHINE_ABSENT, MONTHS, REVIEWED_ABSENT, SEP, byLine, confidenceText, confidenceWord, daysIn, decisionSentence, draftStamp, formatValue, isoDate, optionLabel, rowName, settledValue } from './describe';

export type Locked = { by: string; at: string } | null;

export type FieldTableProps = {
  matterRef: string;
  fields: LoiFieldView[];
  groups: Record<string, string>;
  selectedKey: string | null;
  locked: Locked;
  offline: boolean;
  busyKey: string | null;
  saveFailedKey: string | null;
  onSelect: (key: string | null) => void;
  onDecide: (key: string, input: LoiFieldDecisionInput) => void;
  onShowPage: (page: number, label: string) => void;
  onNext: () => void;
  hasNext: boolean;
  refusedReason: { key: string; reason: string } | null;
};

/**
 * A typed correction or note survives moving to another row. `against` names
 * the saved decision (run and time) the draft was typed over; a draft is
 * restored only while that is still the saved decision, so a save that lands
 * after the row was left, or another reviewer's decision, drops it.
 */
export type FieldDraft = { against: string; choice: Choice | null; text: string; list: string; day: string; month: string; year: string; enumValue: string; note: string; noteOpen: boolean };

const CELL = 'px-3 py-2 align-top text-[13px] leading-[1.3]';

export function FieldTable(props: FieldTableProps) {
  const { matterRef, fields, groups, selectedKey, locked, onSelect } = props;
  const drafts = useRef(new Map<string, FieldDraft>());
  const focusKey = selectedKey && fields.some((f) => f.key === selectedKey) ? selectedKey : fields[0]?.key ?? null;
  const byGroup = useMemo(() => {
    const out = new Map<string, LoiFieldView[]>();
    for (const f of fields) out.set(f.group, [...(out.get(f.group) ?? []), f]);
    return out;
  }, [fields]);

  function onKey(e: KeyboardEvent<HTMLButtonElement>, key: string) {
    const index = fields.findIndex((f) => f.key === key);
    const move = (to: number) => { const target = fields[Math.max(0, Math.min(fields.length - 1, to))]; if (target) { e.preventDefault(); onSelect(target.key); requestAnimationFrame(() => document.getElementById(`loi-row-${target.key}`)?.focus()); } };
    if (e.key === 'ArrowDown') move(index + 1);
    else if (e.key === 'ArrowUp') move(index - 1);
    else if (e.key === 'Home') move(0);
    else if (e.key === 'End') move(fields.length - 1);
    else if (e.key === 'Escape' && selectedKey === key) { e.preventDefault(); onSelect(null); }
  }

  return (
    <div data-slot="field-table" className="overflow-hidden rounded-xl border border-beam-line bg-beam-paper">
      <table className="w-full table-fixed">
        <caption className="sr-only">Extracted fields for {matterRef}: machine value beside reviewed value</caption>
        <colgroup><col className="w-[232px]" /><col /><col /><col className="w-[104px]" /></colgroup>
        <thead className="bg-beam-cream-deep">
          <tr>
            <th scope="col" className="eyebrow h-8 px-3 text-left text-beam-text-soft">Field</th>
            <th scope="col" className="eyebrow h-8 px-3 text-left text-beam-text-soft">Machine</th>
            <th scope="col" className="eyebrow h-8 px-3 text-left text-beam-text-soft">Reviewed</th>
            <th scope="col" className="eyebrow h-8 px-3 text-left text-beam-text-soft">Decision</th>
          </tr>
        </thead>
        <tbody>
          {[...byGroup.entries()].map(([group, list]) => {
            const decided = list.filter((f) => (f.item?.decision ?? 'unresolved') !== 'unresolved').length;
            return [
              <tr key={`g-${group}`} className="border-t border-beam-line bg-beam-cream-deep">
                <th scope="rowgroup" colSpan={4} className="eyebrow h-8 px-3 text-left text-beam-text-muted"><span className="mr-2 text-foreground">{group}</span>{groups[group] ?? group} <span className="tabular ml-2 font-normal normal-case tracking-normal">{list.length} {list.length === 1 ? 'field' : 'fields'}{SEP}{decided} decided</span></th>
              </tr>,
              ...list.flatMap((field) => {
                const selected = field.key === selectedKey;
                const decision = field.item?.decision ?? 'unresolved';
                const machine = field.machine;
                const reviewed = decision === 'unresolved' || decision === 'flagged' ? null : formatValue(field, settledValue(field), REVIEWED_ABSENT);
                const rows = [
                  <tr
                    key={field.key}
                    data-slot="field-row"
                    data-key={field.key}
                    data-decision={decision}
                    aria-current={selected ? 'true' : undefined}
                    onClick={(e) => { if ((e.target as HTMLElement).closest('button')) return; onSelect(selected ? null : field.key); }}
                    className={cn('h-14 cursor-pointer border-t border-l-2 border-beam-line hover:bg-beam-cream-deep', selected ? 'border-l-beam-signature bg-beam-cream-deep' : 'border-l-transparent')}
                  >
                    <td className={CELL}>
                      <button
                        type="button"
                        id={`loi-row-${field.key}`}
                        aria-expanded={selected}
                        aria-controls={`loi-editor-${field.key}`}
                        aria-label={rowName(field)}
                        tabIndex={field.key === focusKey ? 0 : -1}
                        onClick={() => onSelect(selected ? null : field.key)}
                        onKeyDown={(e) => onKey(e, field.key)}
                        className={cn('flex flex-col items-start text-left outline-none focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:ring-offset-2', selected && 'font-medium')}
                      >
                        <span>{field.label}</span>
                        {field.required ? <span className="text-xs font-normal text-beam-text-muted">Required</span> : null}
                      </button>
                    </td>
                    <td className={CELL}>
                      <span className={cn('block truncate', !machine || machine.value === null ? 'text-beam-text-muted' : '')}>{machine ? formatValue(field, machine.value, MACHINE_ABSENT) : 'No extraction'}</span>
                      {machine ? <span className="tabular text-xs text-beam-text-muted">{confidenceText(machine.confidence)}{machine.needsReview ? <span className="font-medium text-beam-warning">{SEP}Machine asks for a check</span> : null}{SEP}{machine.evidence ? `Page ${machine.evidence.page}` : 'No page cited'}</span> : null}
                    </td>
                    <td className={CELL}>
                      <span className={cn('block truncate', reviewed === null || decision === 'missing' ? 'text-beam-text-muted' : '')}>{reviewed ?? 'Not decided'}</span>
                      {field.item && decision !== 'unresolved' ? <span className="text-xs text-beam-text-muted">{byLine(field.item.byName ?? field.item.by, field.item.at, field.item.auto)}</span> : field.item?.prior ? <span className="text-xs text-beam-text-muted">Previous review: {DECISION_WORD[field.item.prior.decision]}{field.item.prior.decision === 'corrected' ? ` to ${formatValue(field, field.item.prior.reviewedValue, REVIEWED_ABSENT)}` : ''}</span> : null}
                    </td>
                    <td className={CELL}>
                      <DecisionMark word={DECISION_WORD[decision]} />
                      {decision === 'missing' && field.required ? <span className="block text-xs font-medium text-beam-warning">blocks approval</span> : null}
                    </td>
                  </tr>,
                ];
                if (selected) {
                  rows.push(
                    <tr key={`${field.key}-editor`} className="border-t border-l-2 border-beam-line border-l-beam-signature">
                      <td colSpan={4} id={`loi-editor-${field.key}`} className="bg-beam-paper p-0">
                        <FieldEditor
                          field={field}
                          locked={locked}
                          offline={props.offline}
                          busy={props.busyKey === field.key}
                          saveFailed={props.saveFailedKey === field.key}
                          refusedReason={props.refusedReason?.key === field.key ? props.refusedReason.reason : null}
                          draft={drafts.current.get(field.key)}
                          onDraft={(d) => { if (d) drafts.current.set(field.key, d); else drafts.current.delete(field.key); }}
                          onClose={() => { onSelect(null); requestAnimationFrame(() => document.getElementById(`loi-row-${field.key}`)?.focus()); }}
                          onDecide={(input) => props.onDecide(field.key, input)}
                          onShowPage={props.onShowPage}
                          onNext={props.onNext}
                          hasNext={props.hasNext}
                        />
                      </td>
                    </tr>,
                  );
                }
                return rows;
              }),
            ];
          })}
        </tbody>
      </table>
    </div>
  );
}

type Choice = 'accepted' | 'corrected' | 'missing' | 'flagged';

function FieldError({ id, text }: { id: string; text: string | null }) {
  return text ? <p id={id} role="alert" className="text-[13px] text-beam-error">{text}</p> : null;
}

const currentYear = () => new Date().getFullYear();

export function FieldEditor({ field, locked, offline, busy, saveFailed, refusedReason, draft: storedDraft, onDraft, onClose, onDecide, onShowPage, onNext, hasNext }: {
  field: LoiFieldView;
  locked: Locked;
  offline: boolean;
  busy: boolean;
  saveFailed: boolean;
  refusedReason: string | null;
  draft: FieldDraft | undefined;
  onDraft: (draft: FieldDraft | null) => void;
  onClose: () => void;
  onDecide: (input: LoiFieldDecisionInput) => void;
  onShowPage: (page: number, label: string) => void;
  onNext: () => void;
  hasNext: boolean;
}) {
  const id = useId();
  const item = field.item;
  const machine = field.machine;
  const machineValue = machine?.value ?? null;
  const saved = item && item.decision !== 'unresolved' ? item : null;
  const initialChoice: Choice | null = saved && saved.decision !== 'unresolved' ? saved.decision : null;
  const stamp = draftStamp(item);
  // A draft typed over a decision that has since been saved or superseded is stale and never shown.
  const draft = storedDraft && storedDraft.against === stamp ? storedDraft : undefined;
  const [choice, setChoice] = useState<Choice | null>(draft?.choice ?? initialChoice);
  const [text, setText] = useState(draft?.text ?? '');
  const [list, setList] = useState(draft?.list ?? '');
  const [day, setDay] = useState(draft?.day ?? '');
  const [month, setMonth] = useState(draft?.month ?? '');
  const [year, setYear] = useState(draft?.year ?? '');
  const [enumValue, setEnumValue] = useState(draft?.enumValue ?? '');
  const [note, setNote] = useState(draft?.note ?? item?.note ?? '');
  const [noteOpen, setNoteOpen] = useState(draft?.noteOpen ?? Boolean(item?.note));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(Boolean(draft));
  const noteRef = useRef<HTMLTextAreaElement>(null);

  const prefillDraft = (d: FieldDraft) => { setChoice(d.choice); setText(d.text); setList(d.list); setDay(d.day); setMonth(d.month); setYear(d.year); setEnumValue(d.enumValue); setNote(d.note); setNoteOpen(d.noteOpen); setErrors({}); setDirty(true); };
  const prefill = (value: LoiValue) => {
    if (field.kind === 'list') setList(Array.isArray(value) ? value.join('\n') : '');
    else if (field.kind === 'date') {
      const m = typeof value === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
      setYear(m ? String(Number(m[1])) : ''); setMonth(m ? String(Number(m[2])) : ''); setDay(m ? String(Number(m[3])) : '');
    } else if (field.kind === 'enum') setEnumValue(typeof value === 'string' && (field.options ?? []).includes(value) ? value : '');
    else setText(typeof value === 'string' ? value : '');
  };

  useEffect(() => {
    // A draft from an earlier visit to this row wins while it was typed over the decision now saved; a decision saved since (here or in another tab) drops it.
    if (draft) { prefillDraft(draft); return; }
    if (storedDraft) onDraft(null);
    setChoice(initialChoice);
    prefill(saved?.decision === 'corrected' ? saved.reviewedValue : machineValue);
    setNote(item?.note ?? '');
    setNoteOpen(Boolean(item?.note));
    setErrors({});
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.key, item?.at, machine?.value]);

  // The draft follows every edit until the decision saves.
  useEffect(() => {
    if (dirty) onDraft({ against: stamp, choice, text, list, day, month, year, enumValue, note, noteOpen });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, choice, text, list, day, month, year, enumValue, note, noteOpen]);

  // A failed click-commit (Accept or Missing) must not leave the segment claiming a decision that was not saved.
  useEffect(() => {
    if (saveFailed && dirty && (choice === 'accepted' || choice === 'missing')) setChoice(initialChoice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveFailed]);

  const machineAbsent = machineValue === null;
  const machineText = formatValue(field, machineValue, MACHINE_ABSENT);
  const settled = settledValue(field);

  if (locked) {
    return (
      <div data-slot="field-editor-locked" className="flex flex-col gap-3 p-4">
        <Pair field={field} machineText={machineText} settled={settled} />
        <Evidence field={field} onShowPage={onShowPage} />
        <p className="text-[13px]">Decisions are locked: the LOI was approved by {locked.by} on {longDate(locked.at)} at {new Date(locked.at).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: false })}.</p>
      </div>
    );
  }

  function currentValue(): { value?: LoiValue; error?: string } {
    switch (field.kind) {
      case 'list': {
        const entries = list.split('\n').map((s) => s.trim()).filter(Boolean);
        if (entries.length === 0) return { error: 'Enter at least one entry, one per line' };
        if (entries.some((e) => e.length > 200)) return { error: 'Each entry is at most 200 characters' };
        return { value: entries };
      }
      case 'date': {
        const d = Number(day), m = Number(month), y = Number(year);
        if (!day || !month || !year) return { error: 'Enter a day, month and year' };
        // An injury may be years old and is never in the future; a letter or appointment date sits near today.
        const [minY, maxY] = field.key === 'dateOfInjury' ? [1950, currentYear()] : [currentYear() - 2, currentYear() + 2];
        if (!Number.isInteger(y) || y < minY || y > maxY) return { error: `Enter a year between ${minY} and ${maxY}` };
        if (!Number.isInteger(d) || d < 1 || d > daysIn(y, m)) return { error: `${MONTHS[m - 1]} ${y} has ${daysIn(y, m)} days` };
        return { value: isoDate(d, m, y) };
      }
      case 'enum':
        if (!enumValue) return { error: field.key === 'scheme' ? 'Choose a scheme' : 'Choose a report type' };
        return { value: enumValue };
      default: {
        const v = text.trim();
        if (!v) return { error: `Enter the ${field.label.toLowerCase()}` };
        if (v.length > 500) return { error: 'At most 500 characters' };
        return { value: v };
      }
    }
  }

  const sameAsMachine = (v: LoiValue) => JSON.stringify(v) === JSON.stringify(machineValue);
  // The value field is in view at all times, prefilled with the machine's value or the saved
  // correction; a value that differs from the machine's is the change, whatever the segment says
  // ("Correct" beside "Accept" read as the adjective, and a hidden field went unfound: D-0080).
  const typed = currentValue();
  const edited = typed.value !== undefined && !sameAsMachine(typed.value);
  const effective: Choice | null = edited ? 'corrected' : choice;
  const pendingCorrection = choice === 'corrected' && !edited && typed.value !== undefined;

  function commit(input: LoiFieldDecisionInput) {
    setErrors({});
    setDirty(true);
    onDecide(input);
  }

  function onChoice(next: Choice) {
    if (busy || offline) return;
    setChoice(next);
    setDirty(true);
    if (next === 'corrected') {
      requestAnimationFrame(() => document.getElementById(`${id}-value`)?.focus());
    } else if (next === 'accepted') {
      if (machineAbsent || edited) return;
      commit({ decision: 'accepted', ...(note.trim() ? { note: note.trim() } : {}) });
    } else if (next === 'missing') {
      commit({ decision: 'missing', ...(note.trim() ? { note: note.trim() } : {}) });
    } else if (next === 'flagged') {
      setNoteOpen(true);
      requestAnimationFrame(() => noteRef.current?.focus());
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || offline) return;
    if (!effective) { setErrors({ choice: 'Choose Accept, Change, Missing or Flag.' }); return; }
    if (effective === 'flagged') {
      if (!note.trim()) { setErrors({ note: 'Say what is ambiguous' }); noteRef.current?.focus(); return; }
      commit({ decision: 'flagged', note: note.trim() });
      return;
    }
    if (effective === 'corrected') {
      const c = currentValue();
      if (c.error) { setErrors({ value: c.error }); requestAnimationFrame(() => document.getElementById(`${id}-value`)?.focus()); return; }
      if (sameAsMachine(c.value!)) { setErrors({ value: "This is the machine's value. Choose Accept." }); return; }
      commit({ decision: 'corrected', reviewedValue: c.value!, ...(note.trim() ? { note: note.trim() } : {}) });
      return;
    }
    // Accept or Missing with a note added afterwards.
    commit({ decision: effective, ...(note.trim() ? { note: note.trim() } : {}) });
  }

  const description = effective === 'accepted' ? "The machine's value is right as read." : effective === 'corrected' ? 'Save the value as the letter has it.' : effective === 'missing' ? `Not in the letter.${field.required ? ' A required field marked missing blocks approval.' : ''}` : effective === 'flagged' ? 'Ambiguous; someone else decides. Blocks approval until re-decided.' : 'Choose Accept, Change, Missing or Flag, or type the value as the letter has it.';
  const saveLabel = busy ? (effective === 'flagged' ? 'Flagging' : 'Saving') : effective === 'flagged' ? 'Flag field' : 'Save change';
  const showSave = effective === 'corrected' || effective === 'flagged';
  const noteAfter = (effective === 'accepted' || effective === 'missing') && saved && note.trim() !== (item?.note ?? '') && note.trim().length > 0;
  const statusText = offline ? 'Paused while offline. Your typed value is kept.' : busy ? 'Saving' : saveFailed && dirty ? 'Not saved: try again.' : saved ? (saved.auto ? decisionSentence(saved.decision, saved.byName ?? saved.by, saved.at, saved.auto) : decisionSentence(saved.decision, saved.byName ?? saved.by, saved.at).replace(/^\w+/, 'Saved')) : 'Not saved yet';
  const savedInert = showSave && saved !== null && !dirty && saved.decision === effective;

  return (
    <form data-slot="field-editor" aria-labelledby={`${id}-legend`} noValidate onSubmit={submit} onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); (e.currentTarget as HTMLFormElement).requestSubmit(); } else if (e.key === 'Escape' && !e.defaultPrevented) { e.preventDefault(); onClose(); } }} className="flex flex-col gap-4 p-4">
      <Pair field={field} machineText={machineText} settled={settled} />
      <Evidence field={field} onShowPage={onShowPage} />
      {item?.prior ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-beam-cream-deep px-3 py-2 text-[13px]">
          <span>Previous decision, run {item.prior.runId.slice(0, 8)}{item.prior.by ? ` by ${item.prior.by}` : ''}: {DECISION_WORD[item.prior.decision]}{item.prior.decision === 'corrected' ? ` to ${formatValue(field, item.prior.reviewedValue, REVIEWED_ABSENT)}` : ''}.</span>
          <Button type="button" variant="outline" size="default" className="h-8" disabled={offline} onClick={() => {
            const p = item.prior!;
            if (p.decision === 'missing') onChoice('missing');
            else if (p.decision === 'accepted' || (p.decision === 'corrected' && sameAsMachine(p.reviewedValue))) onChoice('accepted');
            else if (p.decision === 'corrected') { setChoice('corrected'); prefill(p.reviewedValue); setDirty(true); }
            else { setChoice('flagged'); setNote(p.note ?? ''); setNoteOpen(true); setDirty(true); }
          }}>Use previous decision</Button>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <span id={`${id}-legend`} className="eyebrow text-beam-text-muted">Your decision</span>
        <ToggleGroup type="single" value={effective ?? ''} onValueChange={(v) => { if (v) onChoice(v as Choice); }} aria-label={`Your decision for ${field.label}`} aria-describedby={`${id}-desc`} className="justify-start rounded-lg bg-beam-cream-deep p-1">
          {(['accepted', 'corrected', 'missing', 'flagged'] as Choice[]).map((c) => {
            const disabled = c === 'accepted' && (machineAbsent || edited);
            return (
              <ToggleGroupItem key={c} value={c} aria-disabled={disabled ? 'true' : undefined} aria-describedby={disabled ? (machineAbsent ? `${id}-accept-note` : `${id}-edited-note`) : undefined} onClick={(e) => { if (disabled) { e.preventDefault(); e.stopPropagation(); } }} className={cn('h-9 px-3 text-sm data-[state=on]:bg-beam-paper data-[state=on]:text-foreground data-[state=on]:shadow-beam-1', disabled && 'cursor-default text-beam-text-muted')}>
                {c === 'accepted' ? 'Accept' : c === 'corrected' ? 'Change' : c === 'missing' ? 'Missing' : 'Flag'}
              </ToggleGroupItem>
            );
          })}
        </ToggleGroup>
        <p id={`${id}-desc`} className="text-xs text-beam-text-muted">{description}</p>
        {machineAbsent ? <p id={`${id}-accept-note`} className="text-xs text-beam-text-muted">The machine left this field empty; type the value or mark it missing.</p> : null}
        {edited && !machineAbsent ? <p id={`${id}-edited-note`} className="text-xs text-beam-text-muted">Your value differs from the machine's. Save the change, or restore the machine value.</p> : null}
        <FieldError id={`${id}-choice-error`} text={errors.choice ?? null} />
        <FieldError id={`${id}-refused`} text={refusedReason ? `Refused: ${refusedReason}.` : null} />
      </div>

      <div className="flex flex-col gap-1">
          {field.kind === 'date' ? (
            <>
              <span className="text-sm font-medium">{field.label}</span>
              <div className="grid max-w-[360px] grid-cols-[72px_minmax(0,1fr)_88px] gap-2">
                <div className="flex flex-col gap-1"><Label htmlFor={`${id}-value`}>Day</Label><Input id={`${id}-value`} inputMode="numeric" value={day} onChange={(e) => { setDay(e.target.value); setDirty(true); }} className="h-9" aria-invalid={errors.value ? true : undefined} aria-describedby={errors.value ? `${id}-value-error` : undefined} /></div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor={`${id}-month`}>Month</Label>
                  <Select value={month} onValueChange={(v) => { setMonth(v); setDirty(true); }}>
                    <SelectTrigger id={`${id}-month`} className="h-9" aria-invalid={errors.value ? true : undefined} aria-describedby={errors.value ? `${id}-value-error` : undefined}><SelectValue placeholder="Month" /></SelectTrigger>
                    <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1"><Label htmlFor={`${id}-year`}>Year</Label><Input id={`${id}-year`} inputMode="numeric" value={year} onChange={(e) => { setYear(e.target.value); setDirty(true); }} className="h-9" aria-invalid={errors.value ? true : undefined} aria-describedby={errors.value ? `${id}-value-error` : undefined} /></div>
              </div>
              {day && month && year && !currentValue().error ? <p className="text-xs text-beam-text-muted">{longDate(currentValue().value as string)}</p> : null}
            </>
          ) : field.kind === 'enum' ? (
            <>
              <Label htmlFor={`${id}-value`}>{field.label}</Label>
              <Select value={enumValue} onValueChange={(v) => { setEnumValue(v); setDirty(true); }}>
                <SelectTrigger id={`${id}-value`} className="h-9 max-w-[360px]" aria-invalid={errors.value ? true : undefined} aria-describedby={errors.value ? `${id}-value-error` : undefined}><SelectValue placeholder={field.key === 'scheme' ? 'Choose a scheme' : 'Choose a report type'} /></SelectTrigger>
                <SelectContent>{(field.options ?? []).map((o) => <SelectItem key={o} value={o}>{optionLabel(field.key, o)}</SelectItem>)}</SelectContent>
              </Select>
              {typeof machineValue === 'string' && !(field.options ?? []).includes(machineValue) ? <p className="text-xs text-beam-text-muted">The machine read "{machineValue}", which is not one of the form's options.</p> : null}
            </>
          ) : field.kind === 'list' ? (
            <>
              <Label htmlFor={`${id}-value`}>{field.label}</Label>
              <Textarea id={`${id}-value`} rows={4} value={list} onChange={(e) => { setList(e.target.value); setDirty(true); }} aria-invalid={errors.value ? true : undefined} aria-describedby={`${id}-list-hint${errors.value ? ` ${id}-value-error` : ''}`} />
              <p id={`${id}-list-hint`} className="text-xs text-beam-text-muted">One entry per line. <span className="tabular">{list.split('\n').filter((s) => s.trim()).length}</span> entries</p>
            </>
          ) : (
            <>
              <Label htmlFor={`${id}-value`}>{field.label}</Label>
              <Input id={`${id}-value`} value={text} maxLength={500} onChange={(e) => { setText(e.target.value); setDirty(true); }} className="h-9 max-w-[480px]" aria-invalid={errors.value ? true : undefined} aria-describedby={errors.value ? `${id}-value-error` : undefined} />
              {text.length >= 400 ? <span className="tabular text-xs text-beam-text-muted">{text.length} of 500</span> : null}
            </>
          )}
          <FieldError id={`${id}-value-error`} text={errors.value ?? null} />
          {pendingCorrection && !errors.value ? <p className="text-xs text-beam-text-muted">This is the machine's value. Choose Accept.</p> : null}
          {edited && !machineAbsent ? <button type="button" className="self-start text-[13px] text-beam-signature underline-offset-4 hover:underline" onClick={() => { prefill(machineValue); setDirty(true); }}>Restore machine value</button> : null}
      </div>

      {noteOpen || effective === 'flagged' ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${id}-note`}>{effective === 'flagged' ? 'Note (required to flag)' : 'Note (optional)'}</Label>
          <Textarea ref={noteRef} id={`${id}-note`} rows={3} maxLength={500} value={note} onChange={(e) => { setNote(e.target.value); setDirty(true); }} aria-invalid={errors.note ? true : undefined} aria-describedby={errors.note ? `${id}-note-error` : undefined} className="max-w-[480px]" />
          {note.length >= 400 ? <span className="tabular text-xs text-beam-text-muted">{note.length} of 500</span> : null}
          <FieldError id={`${id}-note-error`} text={errors.note ?? null} />
        </div>
      ) : (
        <Collapsible open={false} onOpenChange={() => setNoteOpen(true)}>
          <CollapsibleTrigger className="flex min-h-8 items-center gap-1 text-[13px] text-beam-text-muted hover:text-foreground"><ChevronRightIcon aria-hidden="true" className="size-4" /> Add a note</CollapsibleTrigger>
          <CollapsibleContent />
        </Collapsible>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {showSave ? (
          <Button type="submit" size="lg" variant={savedInert || pendingCorrection ? 'outline' : 'default'} className={cn('h-9', (savedInert || pendingCorrection) && 'cursor-default text-beam-text-muted hover:bg-beam-paper')} disabled={offline} aria-disabled={busy || savedInert || pendingCorrection ? 'true' : undefined} aria-describedby={`${id}-status`}>{savedInert && !busy ? 'Saved' : saveLabel}</Button>
        ) : noteAfter ? (
          <Button type="submit" size="default" variant="outline" className="h-8" disabled={offline} aria-disabled={busy ? 'true' : undefined}>Save note</Button>
        ) : null}
        <span id={`${id}-status`} className="text-xs text-beam-text-muted">{statusText}</span>
        {hasNext ? <Button type="button" variant="ghost" size="default" className="ml-auto h-8" onClick={onNext}>Next unresolved</Button> : null}
      </div>
    </form>
  );
}

export function Pair({ field, machineText, settled }: { field: LoiFieldView; machineText: string; settled: LoiValue }) {
  const item = field.item;
  const decision = item?.decision ?? 'unresolved';
  const humanText = decision === 'unresolved' || decision === 'flagged' ? 'Not decided' : formatValue(field, settled, REVIEWED_ABSENT);
  return (
    <div data-slot="machine-human" className="grid gap-6 md:grid-cols-2">
      <div className="flex flex-col gap-1">
        <span className="eyebrow text-beam-text-muted">Machine</span>
        <p className={cn('text-[16px] font-medium leading-snug xl:text-[20px]', field.machine?.value === null && 'text-beam-text-muted')}>{machineText}</p>
        {field.machine ? <p className="text-[13px] text-beam-text-muted">Confidence {field.machine.confidence.toFixed(2)}, {confidenceWord(field.machine.confidence)}.{field.machine.needsReview ? ' Machine asks for a check.' : ''}</p> : null}
      </div>
      <div className="flex flex-col gap-1">
        <span className="eyebrow text-beam-text-muted">Human</span>
        <p className={cn('text-[16px] font-medium leading-snug xl:text-[20px]', (humanText === 'Not decided' || decision === 'missing') && 'text-beam-text-muted')}>{humanText}</p>
        {item && decision !== 'unresolved' ? <p className="text-[13px] text-beam-text-muted">{decisionSentence(decision, item.byName ?? item.by, item.at)}</p> : null}
      </div>
    </div>
  );
}

export function Evidence({ field, onShowPage }: { field: Pick<LoiFieldView, 'label' | 'machine'>; onShowPage: (page: number, label: string) => void }) {
  const ev = field.machine?.evidence;
  if (!field.machine) return null;
  return (
    <p className="text-[13px]">
      {ev ? <>Evidence, page {ev.page}: <q className="text-beam-text-soft">{ev.quote}</q>{' '}<button type="button" className="font-medium text-beam-signature underline-offset-4 hover:underline" aria-label={`Show page ${ev.page} of the letter, the evidence cited for ${field.label}`} onClick={() => onShowPage(ev.page, field.label)}>Show page {ev.page}</button></> : <span className="text-beam-text-muted">No evidence cited: the machine did not point to a page.</span>}
    </p>
  );
}
